import { ChangeDetectionStrategy, Component, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AppLang, LanguageService } from 'src/app/services/language.service';

interface LangOption {
  code: AppLang;
  label: string;       // nombre del idioma en su propio idioma
  short: string;       // código corto que se ve en el botón
}

const LANG_OPTIONS: LangOption[] = [
  { code: 'es', label: 'Español',   short: 'ES' },
  { code: 'en', label: 'English',   short: 'EN' },
  { code: 'pt', label: 'Português', short: 'PT' },
];

/**
 * Switcher de idioma — botón compacto (flag + código corto) con popover de 3
 * opciones que se cierra al click afuera. Usa el mismo lenguaje visual que
 * `theme-switcher`: tokens `--base-text-color`, `--crm-accent-1`, `--base-bg`,
 * mismo radius/spacing/animación. Funciona en header (autenticado) y en login.
 */
@Component({
  selector: 'language-switcher',
  template: `
    <!-- SVG sprite con las banderas: en Windows desktop los emoji 🇪🇸/🇬🇧/🇧🇷
         no renderizan (no hay glyphs), así que usamos SVGs reales. -->
    <svg width="0" height="0" style="position:absolute" aria-hidden="true">
      <defs>
        <symbol id="lang-flag-es" viewBox="0 0 9 6">
          <rect width="9" height="6" fill="#c60b1e"/>
          <rect y="1.5" width="9" height="3" fill="#ffc400"/>
        </symbol>
        <symbol id="lang-flag-en" viewBox="0 0 60 30">
          <clipPath id="lang-flag-en-clip"><path d="M0,0 v30 h60 v-30 z"/></clipPath>
          <rect width="60" height="30" fill="#012169"/>
          <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" stroke-width="6"/>
          <path d="M0,0 L60,30 M60,0 L0,30" clip-path="url(#lang-flag-en-clip)" stroke="#C8102E" stroke-width="4"/>
          <path d="M30,0 v30 M0,15 h60" stroke="#fff" stroke-width="10"/>
          <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" stroke-width="6"/>
        </symbol>
        <symbol id="lang-flag-pt" viewBox="0 0 14 10">
          <rect width="14" height="10" fill="#009c3b"/>
          <polygon points="7,1 13,5 7,9 1,5" fill="#ffdf00"/>
          <circle cx="7" cy="5" r="2" fill="#002776"/>
        </symbol>
      </defs>
    </svg>

    <div class="lang-switcher" #root>
      <button
        type="button"
        class="lang-trigger"
        [class.is-open]="open()"
        [attr.aria-label]="'Idioma · ' + activeOption().label"
        [attr.aria-expanded]="open()"
        [title]="activeOption().label"
        (click)="togglePopover($event)">
        <svg class="lang-flag" aria-hidden="true">
          <use [attr.href]="'#lang-flag-' + activeOption().code"></use>
        </svg>
        <span class="lang-code">{{ activeOption().short }}</span>
        <i class="dx-icon dx-icon-chevrondown lang-chevron" aria-hidden="true"></i>
      </button>

      <div class="lang-popover" *ngIf="open()" role="menu">
        <div class="lang-popover-title">Idioma</div>
        <ul class="lang-list">
          <li *ngFor="let opt of options">
            <button
              type="button"
              class="lang-option"
              role="menuitem"
              [class.active]="opt.code === current()"
              (click)="pick(opt.code)">
              <svg class="lang-option-flag" aria-hidden="true">
                <use [attr.href]="'#lang-flag-' + opt.code"></use>
              </svg>
              <span class="lang-option-meta">
                <span class="lang-option-label">{{ opt.label }}</span>
                <span class="lang-option-short">{{ opt.short }}</span>
              </span>
              <span class="lang-option-check" *ngIf="opt.code === current()" aria-hidden="true">
                <i class="dx-icon dx-icon-check"></i>
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  `,
  styles: [`
    .lang-switcher {
      display: inline-flex;
      align-items: center;
      position: relative;
    }

    /* === Trigger: pildora compacta con flag + code === */
    .lang-trigger {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      background: color-mix(in srgb, var(--base-text-color, #333) 4%, transparent);
      border: 1px solid color-mix(in srgb, var(--base-text-color, #333) 10%, transparent);
      border-radius: 999px;
      cursor: pointer;
      font: inherit;
      color: var(--base-text-color, #222);
      transition: all 0.16s cubic-bezier(0.4, 0, 0.2, 1);

      .lang-flag {
        width: 18px;
        height: 13px;
        border-radius: 2px;
        box-shadow: 0 0 0 1px color-mix(in srgb, var(--base-text-color, #333) 22%, transparent);
        flex-shrink: 0;
        display: inline-block;
        overflow: hidden;
      }
      .lang-code {
        font-size: var(--text-xs, 11px);
        font-weight: 700;
        letter-spacing: 0.6px;
      }
      .lang-chevron {
        font-size: var(--text-sm, 12px);
        opacity: 0.55;
        transition: transform 0.18s ease, opacity 0.18s ease;
      }

      &:hover {
        background: color-mix(in srgb, var(--crm-accent-1, #1890ff) 8%, transparent);
        border-color: color-mix(in srgb, var(--crm-accent-1, #1890ff) 28%, transparent);
        .lang-chevron { opacity: 0.85; }
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--crm-accent-1, #1890ff) 28%, transparent);
      }

      &.is-open {
        background: color-mix(in srgb, var(--crm-accent-1, #1890ff) 12%, transparent);
        border-color: color-mix(in srgb, var(--crm-accent-1, #1890ff) 36%, transparent);
        .lang-chevron { transform: rotate(180deg); opacity: 0.9; }
      }
    }

    /* === Popover: lista de opciones === */
    .lang-popover {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      min-width: 200px;
      padding: 10px;
      border-radius: 14px;
      z-index: 1500;
      background: var(--card-background, var(--base-bg, #fff));
      border: 1px solid color-mix(in srgb, var(--base-text-color, #333) 12%, transparent);
      box-shadow:
        0 24px 60px rgba(0, 0, 0, 0.18),
        0 4px 14px rgba(0, 0, 0, 0.06);
      animation: lang-pop 0.18s cubic-bezier(0.4, 0, 0.2, 1);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
    }
    @keyframes lang-pop {
      from { opacity: 0; transform: translateY(-6px) scale(0.98); }
      to   { opacity: 1; transform: translateY(0) scale(1); }
    }

    .lang-popover-title {
      font-size: var(--text-xs, 11px);
      font-weight: 700;
      letter-spacing: 1.6px;
      text-transform: uppercase;
      color: color-mix(in srgb, var(--base-text-color, #333) 55%, transparent);
      padding: 4px 6px 8px;
    }

    .lang-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .lang-option {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 8px 10px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: 9px;
      cursor: pointer;
      font: inherit;
      color: var(--base-text-color, #222);
      text-align: left;
      transition: all 0.14s cubic-bezier(0.4, 0, 0.2, 1);

      .lang-option-flag {
        width: 22px;
        height: 16px;
        border-radius: 2px;
        box-shadow: 0 0 0 1px color-mix(in srgb, var(--base-text-color, #333) 22%, transparent);
        flex-shrink: 0;
        display: inline-block;
        overflow: hidden;
      }
      .lang-option-meta {
        flex: 1;
        display: inline-flex;
        align-items: baseline;
        justify-content: space-between;
        gap: 8px;
        min-width: 0;
      }
      .lang-option-label {
        font-size: var(--text-base, 14px);
        font-weight: 500;
      }
      .lang-option-short {
        font-size: var(--text-xs, 11px);
        font-weight: 600;
        letter-spacing: 0.6px;
        opacity: 0.55;
      }
      .lang-option-check {
        flex-shrink: 0;
        color: var(--crm-accent-1, #1890ff);
        display: inline-flex;
        align-items: center;
        .dx-icon { font-size: var(--text-base, 14px); }
      }

      &:hover {
        background: color-mix(in srgb, var(--crm-accent-1, #1890ff) 8%, transparent);
        border-color: color-mix(in srgb, var(--crm-accent-1, #1890ff) 22%, transparent);
        transform: translateX(1px);
      }

      &.active {
        background: color-mix(in srgb, var(--crm-accent-1, #1890ff) 12%, transparent);
        border-color: color-mix(in srgb, var(--crm-accent-1, #1890ff) 32%, transparent);
        box-shadow: 0 0 0 1px var(--crm-accent-1, #1890ff) inset;
        .lang-option-label { font-weight: 600; }
        .lang-option-short { opacity: 0.8; }
      }

      &:focus-visible {
        outline: none;
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--crm-accent-1, #1890ff) 28%, transparent);
      }
    }
  `],
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherComponent {
  private langService = inject(LanguageService);
  private host = inject(ElementRef);

  options = LANG_OPTIONS;
  open = signal(false);

  /** Idioma activo. Se sincroniza con `lang$` del service. */
  current = signal<AppLang>(this.langService.current);
  activeOption = computed<LangOption>(
    () => LANG_OPTIONS.find(o => o.code === this.current()) ?? LANG_OPTIONS[0],
  );

  constructor() {
    // Reflejamos cambios de idioma originados en otra parte de la app (logout, etc.).
    this.langService.lang$.subscribe(lang => this.current.set(lang));
  }

  togglePopover(ev: Event): void {
    ev.stopPropagation();
    this.open.update(v => !v);
  }

  pick(code: AppLang): void {
    this.open.set(false);
    if (code === this.current()) return;
    this.langService.cambiarIdioma(code);
  }

  /** Cierra el popover al click afuera del componente. */
  @HostListener('document:click', ['$event'])
  onDocClick(ev: MouseEvent) {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(ev.target)) this.open.set(false);
  }
}
